import { IAccount, IAccountMappingTemplateGroup, IGroupedAccount, ItemType } from "@modeso/types__aumico-lib-annualreport-be";
import { ISplitGroupAccount } from "@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/IGroupingMasking";
import { GroupingPathHelper } from './GroupingPathHelper';

export class SplitAccountHelper {
    /**
     * Creates a preview of how an account would be split.
     * This is a simplified example. The actual logic might be more complex.
     * @param account The account to be split.
     * @returns An object with active and passive account previews.
     */
    static getSplit(account: IAccount | IGroupedAccount, index: number, nameActive: string = '', namePassive: string = '') {
        // TODO: Trim the names to avoid trailing spaces (e.g., "Split " vs "Split")
        const activeName = nameActive ? `${account.name} ${nameActive}` : account.name;
        const passiveName = namePassive ? `${account.name} ${namePassive}` : account.name;
        if (index === 0) {
            // Asset logic
            // sum up all values
            // active account has the positive balances
            // passive account has the negative balances

            const activeAccount = {
                name: activeName,
                calculatedClosingBalance: account.calculatedClosingBalance > 0 ? account.calculatedClosingBalance : 0,
                calculatedOpeningBalance: account.calculatedOpeningBalance > 0 ? account.calculatedOpeningBalance : 0,
                closingBalance: account.calculatedClosingBalance > 0 ? account.closingBalance : 0,
                openingBalance: account.calculatedOpeningBalance > 0 ? account.openingBalance : 0,

            };

            const passiveAccount = {
                name: passiveName,
                calculatedClosingBalance: account.calculatedClosingBalance > 0 ? 0 : -account.calculatedClosingBalance,
                calculatedOpeningBalance: account.calculatedOpeningBalance > 0 ? 0 : -account.calculatedOpeningBalance,
                closingBalance: account.calculatedClosingBalance > 0 ? 0 : account.closingBalance,
                openingBalance: account.calculatedOpeningBalance > 0 ? 0 : account.openingBalance,
            };

            return { activeAccount, passiveAccount };

        } else if (index === 1) {
            // Liability logic
            // sum up all values
            // passive account has the positive balances
            // active account has the negative balances
            const passiveAccount = {
                name: passiveName,
                calculatedClosingBalance: account.calculatedClosingBalance > 0 ? account.calculatedClosingBalance : 0,
                calculatedOpeningBalance: account.calculatedOpeningBalance > 0 ? account.calculatedOpeningBalance : 0,
                closingBalance: account.calculatedClosingBalance > 0 ? account.closingBalance : 0,
                openingBalance: account.calculatedOpeningBalance > 0 ? account.openingBalance : 0,

            };

            const activeAccount = {
                name: activeName,
                calculatedClosingBalance: account.calculatedClosingBalance > 0 ? 0 : -account.calculatedClosingBalance,
                calculatedOpeningBalance: account.calculatedOpeningBalance > 0 ? 0 : -account.calculatedOpeningBalance,
                closingBalance: account.calculatedClosingBalance > 0 ? 0 : account.closingBalance,
                openingBalance: account.calculatedOpeningBalance > 0 ? 0 : account.openingBalance,
            };

            return { activeAccount, passiveAccount };
        } else {
            throw new Error(`FLI type not supported for splitting: ${index}`);

        }
    }

    static getFinancialLineItems(group: IAccountMappingTemplateGroup): { id: string, name: string, originalItem: any }[] {
        let items: { id: string, name: string, originalItem: any }[] = [];
        if (!group || !group.sortedItems) {
            return items;
        }

        function recurse(item: any) {
            if (item.type === ItemType.FLI || item.type === ItemType.SUB_FLI) {
                items.push({ id: item.name, name: item.caption, originalItem: item });
            }

            if (item.sortedItems) {
                for (const subItem of item.sortedItems) {
                    recurse(subItem);
                }
            } else if (item.accounts) {
                for (const acc of item.accounts) {
                    if (acc.type === ItemType.SUB_FLI) {
                        recurse(acc);
                    }
                }
            }
        }

        for (const topLevelItem of group.sortedItems) {
            recurse(topLevelItem);
        }

        return items;
    }

    static findSplitAccountById(splitAccounts: Record<string, ISplitGroupAccount>, id: string): ISplitGroupAccount | undefined {
        return splitAccounts[id];
    }

    static applySplitAccounts(
        group: IAccountMappingTemplateGroup,
        splitAccounts: Record<string, ISplitGroupAccount>
    ): IAccountMappingTemplateGroup {
        if (!group || !splitAccounts || Object.keys(splitAccounts).length === 0) {
            return group;
        }

        const newGroup = JSON.parse(JSON.stringify(group)); // Deep clone to avoid side effects

        for (const originalAccountId in splitAccounts) {
            const splitDef = splitAccounts[originalAccountId];
            const targetFliId = splitDef.counterpartFli;

            let originalGroupedAccount: IGroupedAccount | null = null;
            let originalFli: any = null;

            // Find the original grouped account that was split
            const findOriginalAccount = (item: any) => {
                if (item.accounts) {
                    const acc = item.accounts.find(a => a.groupId === originalAccountId);
                    if (acc) {
                        originalGroupedAccount = acc;
                        originalFli = item;
                        return; // Found it
                    }
                }
                if (item.sortedItems) {
                    for (const subItem of item.sortedItems) {
                        findOriginalAccount(subItem);
                        if (originalGroupedAccount) return;
                    }
                }
                if (item.accounts) {
                    for (const acc of item.accounts) {
                        if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                            findOriginalAccount(acc);
                            if (originalGroupedAccount) return;
                        }
                    }
                }
            };

            findOriginalAccount(newGroup);

            if (originalGroupedAccount && originalFli) {
                // Mark the original account
                originalGroupedAccount.isGroupedSplitAccount = true;

                // Find the target FLI for the counterpart
                const targetFli = GroupingPathHelper.findFliBySortingId(newGroup, targetFliId);

                if (targetFli) {
                    // Determine if asset (index 0) or liability (index 1) based on rowNo of the main group
                    const mainGroupIndex = newGroup.sortedItems.findIndex(mainGroup =>
                        mainGroup.groups?.some(g => g.rowNo === originalFli.rowNo) || // Check nested groups
                        mainGroup.rowNo === originalFli.rowNo // Check direct FLIs
                    );


                    const { activeAccount, passiveAccount } = this.getSplit(originalGroupedAccount, mainGroupIndex);


                    // FIX 2: Create a unique ID for the counterpart
                    const counterpartId = `${originalGroupedAccount.groupId}_counterpart`;
                    const counterpartAccount: IGroupedAccount = {
                        ...(originalGroupedAccount as IGroupedAccount),
                        accountNumber: counterpartId,
                        name: passiveAccount.name,
                        isGroupedSplitAccountCounterpart: true,
                        isGroupedSplitAccount: false, // It's the counterpart, not the original split
                        openingBalance: passiveAccount.openingBalance,
                        closingBalance: passiveAccount.closingBalance,
                        calculatedOpeningBalance: passiveAccount.calculatedOpeningBalance,
                        calculatedClosingBalance: passiveAccount.calculatedClosingBalance,

                        accounts: [...originalGroupedAccount.accounts], // Counterpart has no sub-accounts
                        groupId: `${counterpartId}`,
                    };

                    // Update original account to be the active part
                    originalGroupedAccount.name = activeAccount.name;
                    originalGroupedAccount.openingBalance = activeAccount.openingBalance;
                    originalGroupedAccount.closingBalance = activeAccount.closingBalance;
                    originalGroupedAccount.calculatedOpeningBalance = activeAccount.calculatedOpeningBalance;
                    originalGroupedAccount.calculatedClosingBalance = activeAccount.calculatedClosingBalance;

                    if (!targetFli.accounts) {
                        targetFli.accounts = [];
                    }
                    targetFli.accounts.push(counterpartAccount);
                }
            }
        }

        return newGroup;
    }
}
